import { format, parse, isAfter, isBefore, isEqual } from 'date-fns';
import { BusinessHours } from "../types/store";

/**
 * Check if a store is open on a specific date and time
 * @param businessHours - The store's business hours object
 * @param date - The date to check (defaults to now)
 * @param bufferMinutes - Minutes before closing to consider as "closed" for new orders
 * @returns boolean indicating if the store is open
 */
export function isStoreOpen(
  businessHours: BusinessHours | null,
  date: Date = new Date(),
  bufferMinutes: number = 30
): boolean {
  if (!businessHours) return false;

  // Use Australian timezone to ensure consistent day calculation across localhost and Vercel
  const australianDate = new Date(date.toLocaleString("en-US", { timeZone: "Australia/Sydney" }));
  const dayOfWeek = format(australianDate, 'EEEE').toLowerCase();
  const dayHours = businessHours[dayOfWeek];

  if (!dayHours || dayHours.closed) {
    return false;
  }

  // Parse open and close times
  const currentTime = format(australianDate, 'HH:mm');
  const openTime = dayHours.open;
  const closeTime = dayHours.close;

  // Create Date objects for comparison
  const current = parse(currentTime, 'HH:mm', new Date());
  const open = parse(openTime, 'HH:mm', new Date());
  const close = parse(closeTime, 'HH:mm', new Date());
  
  // Subtract buffer time from closing
  const closeWithBuffer = new Date(close.getTime() - bufferMinutes * 60000);

  // Check if current time is between open and close (with buffer)
  return (isAfter(current, open) || isEqual(current, open)) && 
         isBefore(current, closeWithBuffer);
}

/**
 * Check if a store is open on a specific date (ignoring time)
 * @param businessHours - The store's business hours object
 * @param date - The date to check
 * @returns boolean indicating if the store is open on that day
 */
export function isStoreOpenOnDate(
  businessHours: BusinessHours | null,
  date: Date
): boolean {
  if (!businessHours) return false;

  // Use Australian timezone to ensure consistent day calculation across localhost and Vercel
  const australianDate = new Date(date.toLocaleString("en-US", { timeZone: "Australia/Sydney" }));
  const dayOfWeek = format(australianDate, 'EEEE').toLowerCase();
  const dayHours = businessHours[dayOfWeek];

  return dayHours ? !dayHours.closed : false;
}

/**
 * Get formatted business hours for a specific day
 * @param businessHours - The store's business hours object
 * @param date - The date to get hours for
 * @returns Formatted string like "9:00 AM - 5:00 PM" or "Closed"
 */
export function getFormattedHours(
  businessHours: BusinessHours | null,
  date: Date
): string {
  if (!businessHours) return 'Hours not available';

  // Use Australian timezone to ensure consistent day calculation across localhost and Vercel
  const australianDate = new Date(date.toLocaleString("en-US", { timeZone: "Australia/Sydney" }));
  const dayOfWeek = format(australianDate, 'EEEE').toLowerCase();
  const dayHours = businessHours[dayOfWeek];

  if (!dayHours || dayHours.closed) {
    return 'Closed';
  }

  // Convert 24-hour to 12-hour format
  const openTime = parse(dayHours.open, 'HH:mm', new Date());
  const closeTime = parse(dayHours.close, 'HH:mm', new Date());

  return `${format(openTime, 'h:mm a')} - ${format(closeTime, 'h:mm a')}`;
}

/**
 * Get the next available pickup date for a store
 * @param businessHours - The store's business hours object
 * @param startDate - The date to start checking from (defaults to today)
 * @param maxDays - Maximum days to check ahead
 * @returns The next available date or null if none found
 */
export function getNextAvailablePickupDate(
  businessHours: BusinessHours | null,
  startDate: Date = new Date(),
  maxDays: number = 30
): Date | null {
  if (!businessHours) return null;

  const checkDate = new Date(startDate);
  
  for (let i = 0; i < maxDays; i++) {
    if (isStoreOpenOnDate(businessHours, checkDate)) {
      // If it's today, check if we're still within operating hours
      if (i === 0 && !isStoreOpen(businessHours, checkDate)) {
        checkDate.setDate(checkDate.getDate() + 1);
        continue;
      }
      return checkDate;
    }
    checkDate.setDate(checkDate.getDate() + 1);
  }

  return null;
}

/**
 * Gets the next time a store will be open
 * @param businessHours The business hours object containing days of the week
 * @returns string with day and time or null if closed all week
 */
export const getNextOpenTime = (businessHours: Record<string, BusinessHours>): string | null => {
  if (!businessHours) return null;

  const now = new Date();
  // Use Australian timezone to ensure consistent day calculation across localhost and Vercel
  const australianNow = new Date(now.toLocaleString("en-US", { timeZone: "Australia/Sydney" }));
  const days = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  const currentDayIndex = australianNow.getDay();
  
  // Check the current day first
  const currentDay = days[currentDayIndex];
  const currentHours = businessHours[currentDay];
  
  const currentTime = australianNow.toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  });
  
  // If the store opens later today
  if (currentHours && !currentHours.closed && currentTime < currentHours.open) {
    return `Today at ${formatTime(currentHours.open)}`;
  }
  
  // Check the next 7 days (including today)
  for (let i = 1; i <= 7; i++) {
    const nextDayIndex = (currentDayIndex + i) % 7;
    const nextDay = days[nextDayIndex];
    const nextDayHours = businessHours[nextDay];
    
    if (nextDayHours && !nextDayHours.closed) {
      const dayName = i === 1 ? "Tomorrow" : nextDay.charAt(0).toUpperCase() + nextDay.slice(1);
      return `${dayName} at ${formatTime(nextDayHours.open)}`;
    }
  }
  
  return null; // Closed all week
};

/**
 * Formats a time string from 24-hour format to 12-hour format
 * @param time Time string in 24-hour format (HH:MM)
 * @returns Time string in 12-hour format with AM/PM
 */
export const formatTime = (time: string): string => {
  const [hours, minutes] = time.split(":");
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

export const getFormattedBusinessHours = (businessHours: Record<string, BusinessHours>): string => {
  if (!businessHours) return "No business hours set";

  const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
  const formattedHours = days.map((day) => {
    const hours = businessHours[day];
    if (!hours || hours.closed) {
      return `${day.charAt(0).toUpperCase() + day.slice(1)}: Closed`;
    }
    return `${day.charAt(0).toUpperCase() + day.slice(1)}: ${formatTime(hours.open)} - ${formatTime(hours.close)}`;
  });

  return formattedHours.join("\n");
}; 